import { useState, useCallback } from 'react';
import { getApiUrl } from '../config';
import { logger } from '../utils/logger';

export interface ReferralMilestone {
  count: number;
  badge: string;
  tags: string[];
}

export interface ReferralStats {
  referralCode: string | null;
  totalReferrals: number;
  unlockedTags: string[];
  allExclusiveTags: string[];
  milestones: ReferralMilestone[];
  nextMilestone: ReferralMilestone | null;
  remainingForNext: number;
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// El link lleva ?ref= para que el código se aplique solo al registrarse (sin
// tipearlo). Mantenemos el código en el texto como fallback manual.
const SHARE_URL = (code: string) => `https://my-futbol-app.vercel.app/?ref=${code}`;
const SHARE_TEXT = (code: string) =>
  `Unite a Futbolero y ambos ganamos tags exclusivos! Entrá con mi link: ${SHARE_URL(code)} (o usá mi codigo ${code})`;

export function useReferrals() {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiUrl('/api/referrals/my-stats'), { headers: authHeaders() });
      if (res.ok) {
        const data: ReferralStats = await res.json();
        setStats(data);
        setReferralCode(data.referralCode);
      }
    } catch (error) {
      logger.error('Error fetching referral stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getOrCreateCode = useCallback(async (): Promise<string | null> => {
    try {
      const res = await fetch(getApiUrl('/api/referrals/code'), { headers: authHeaders() });
      if (!res.ok) return null;
      const data: { referralCode: string } = await res.json();
      if (data.referralCode) {
        setReferralCode(data.referralCode);
        return data.referralCode;
      }
      return null;
    } catch (error) {
      logger.error('Error getting referral code:', error);
      return null;
    }
  }, []);

  const ensureCode = useCallback(async (): Promise<string | null> => {
    if (referralCode) return referralCode;
    return getOrCreateCode();
  }, [referralCode, getOrCreateCode]);

  const copyCode = useCallback(async (): Promise<boolean> => {
    const code = await ensureCode();
    if (!code) return false;
    try {
      await navigator.clipboard.writeText(code);
      return true;
    } catch {
      return false;
    }
  }, [ensureCode]);

  const shareCode = useCallback(async (): Promise<'shared' | 'copied' | 'failed'> => {
    const code = await ensureCode();
    if (!code) return 'failed';
    const text = SHARE_TEXT(code);

    // Try Web Share API first (mobile browsers + some desktop)
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ text, url: SHARE_URL(code) });
        return 'shared';
      } catch (err: any) {
        // User cancelled — silent; other errors fall through to clipboard
        if (err?.name === 'AbortError') return 'failed';
      }
    }

    // Fallback: copy share text to clipboard
    try {
      await navigator.clipboard.writeText(text);
      return 'copied';
    } catch {
      return 'failed';
    }
  }, [ensureCode]);

  const shareViaWhatsApp = useCallback(async (): Promise<boolean> => {
    const code = await ensureCode();
    if (!code) return false;
    const text = encodeURIComponent(SHARE_TEXT(code));
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
    return true;
  }, [ensureCode]);

  const trackReferral = useCallback(async (
    code: string
  ): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const res = await fetch(getApiUrl('/api/referrals/track'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ referralCode: code }),
      });
      const data = await res.json();
      if (!res.ok) {
        return { success: false, error: data?.error || 'Error registrando referido' };
      }
      return { success: true, message: data?.message };
    } catch (error: any) {
      return { success: false, error: error?.message || 'Error de conexión' };
    }
  }, []);

  return {
    stats,
    loading,
    referralCode,
    fetchStats,
    getOrCreateCode,
    copyCode,
    shareCode,
    shareViaWhatsApp,
    trackReferral,
  };
}
